import InsightsService from '@/services/InsightsServiceV2'
import moment from 'moment-timezone'
import { isEqual } from 'lodash-es'

export default {
  namespaced: true,
  state: {
    categories: [],
    classifications: [],
    feedbackCount: 0,
    totalFeedbackCount: 0,
    negativeClassifications: [],
    companyWideNegativeClassifications: [],
    positiveClassificationsByCategory: {},
    negativeClassificationsByCategory: {},
    totalMentions: [],
    overallMentionsByDaypart: [],
    filters: {},
    topDrivers: [],
    summaries: {},
    lastSummaryURL: '/insights/v2/summary', // only needed for iframe
    feedbackBreakdown: [],
    mentionsOverTime: [],
    feedbackDropdown: []
  },
  getters: {
    selectLastSummaryURL: (state) => state.lastSummaryURL,
    selectClassifications(state) {
      return state.classifications
    },
    selectPositiveClassificationsByCategory(state) {
      return state.positiveClassificationsByCategory
    },
    selectNegativeClassificationsByCategory(state) {
      return state.negativeClassificationsByCategory
    },
    selectFilters(state) {
      return state.filters
    },
    selectNonOtherCategories(state) {
      return state.categories.filter((c) => c.name !== 'Other')
    },
    selectOtherCategoryIds(state) {
      return state.categories.filter((c) => c.name === 'Other').map((c) => c._id)
    },
    selectParentCategoryIds(_, getters) {
      return getters.selectNonOtherCategories.reduce((acc, curr) => {
        if (!curr.path) acc.push(curr._id)
        return acc
      }, [])
    },
    selectCategoryParentMap(_, getters) {
      // TODO add support for paths multiple layers deep
      return getters.selectNonOtherCategories.reduce((acc, curr) => {
        if (!curr.path) {
          acc[curr._id] = null
        } else {
          acc[curr._id] = curr.path.split('/').pop()
        }
        return acc
      }, {})
    },
    selectCategoryNames(_, getters) {
      return getters.selectNonOtherCategories.reduce(
        (acc, curr) => {
          if (!curr.path) {
            acc.push(curr.name)
          }
          return acc
        },
        ['Overall']
      )
    },
    selectHeatmapCategories(_, getters) {
      return getters.selectNonOtherCategories.reduce(
        (acc, curr) => {
          if (!curr.path) acc.push(curr._id)
          return acc
        },
        ['Feedback']
      )
    },
    selectCategory: (state) => (id) => {
      return state.categories.find((el) => el._id === id)
    },
    selectCategoryName: (_, getters) => (id) => {
      return ['Overall', 'Feedback'].includes(id)
        ? id
        : getters.selectNonOtherCategories.find((el) => el._id === id)?.name
    },
    selectTopStrengths(_, getters) {
      const allPcs = getters.selectPositiveClassificationsByCategory

      // Filter out parent categories
      const pcs = Object.fromEntries(
        Object.entries(allPcs).filter(([key]) => !getters.selectParentCategoryIds.includes(key))
      )

      // Sort by positiveMentionCount
      let sorted = Object.keys(pcs).sort((a, b) => {
        return (
          pcs[b].classifications[0].positiveMentionCount -
            pcs[a].classifications[0].positiveMentionCount || pcs[a].name.localeCompare(pcs[b].name)
        )
      })

      // Return top 3 with the same format as original
      return sorted.slice(0, 3).map((id) => {
        const category = getters.selectCategory(id)
        if (category.path === '') {
          return {
            id: category._id,
            name: category.name,
            mentions: pcs[id].classifications[0].positiveMentionCount
          }
        } else {
          const parentCategory = getters.selectCategory(category.path.split('/').pop())
          return {
            id: category._id,
            name: `${parentCategory.name}: ${category.name}`,
            mentions: pcs[id].classifications[0].positiveMentionCount
          }
        }
      })
    },
    selectTopIssues(state, getters) {
      const allNcs = getters.selectNegativeClassificationsByCategory

      // Filter out parent categories
      const ncs = Object.fromEntries(
        Object.entries(allNcs).filter(([key]) => !getters.selectParentCategoryIds.includes(key))
      )

      // Sort by negativeMentionCount
      let sorted = Object.keys(ncs).sort((a, b) => {
        return (
          ncs[b].classifications[0].negativeMentionCount -
            ncs[a].classifications[0].negativeMentionCount || ncs[a].name.localeCompare(ncs[b].name)
        )
      })

      // Return top 3 with the same format as original
      return sorted.slice(0, 3).map((id) => {
        const category = getters.selectCategory(id)
        if (category?.path === '') {
          return {
            id: category?._id,
            name: category?.name,
            mentions: ncs[id]?.classifications[0]?.negativeMentionCount
          }
        } else {
          const parentCategory = getters.selectCategory(category?.path?.split('/').pop())
          return {
            id: category?._id,
            name: `${parentCategory?.name}: ${category?.name}`,
            mentions: ncs[id]?.classifications[0]?.negativeMentionCount
          }
        }
      })
    },
    selectTotalMentions(state) {
      if (!state.mentionsOverTime.length) return []

      // Initialize arrays for each day of the week
      const negativeData = new Array(7).fill(0)
      const positiveData = new Array(7).fill(0)

      // Process each day's data
      state.mentionsOverTime.forEach((dayData) => {
        const dayIndex = moment(dayData.date).day()
        negativeData[dayIndex] += dayData.negativeMentionCount
        positiveData[dayIndex] += dayData.positiveMentionCount
      })

      return [
        { name: 'Negative', data: negativeData },
        { name: 'Positive', data: positiveData }
      ]
    },
    selectIsLocationFiltered(state) {
      return !!state.filters?.locations?.length
    },
    /**
     * If dayNumber is undefined it returns overall, else dynamically calculates by the dayNumber
     * @param {*} state
     * @returns
     */
    selectMentionsByDaypart: (state, getters, rootState, rootGetters) => (dayNumber) => {
      if (!state.mentionsOverTime.length) return []
      // If no specific day is selected, aggregate all days
      if (!dayNumber && dayNumber !== 0) {
        const aggregatedData = {
          morning: { positive: 0, negative: 0 },
          afternoon: { positive: 0, negative: 0 },
          evening: { positive: 0, negative: 0 },
          night: { positive: 0, negative: 0 }
        }

        state.mentionsOverTime.forEach((dayData) => {
          dayData.part.forEach((part, index) => {
            const partKey = part.toLowerCase()
            aggregatedData[partKey].positive += dayData.positiveMentionCount
            aggregatedData[partKey].negative += dayData.negativeMentionCount
          })
        })

        const totalPositive = Object.values(aggregatedData).reduce(
          (sum, part) => sum + part.positive,
          0
        )
        const totalNegative = Object.values(aggregatedData).reduce(
          (sum, part) => sum + part.negative,
          0
        )

        if (!totalPositive && !totalNegative) return []

        return [
          {
            name: 'Negative',
            data: ['morning', 'afternoon', 'evening'].map((part) =>
              totalNegative ? Math.round((aggregatedData[part].negative / totalNegative) * 100) : 0
            )
          },
          {
            name: 'Positive',
            data: ['morning', 'afternoon', 'evening'].map((part) =>
              totalPositive ? Math.round((aggregatedData[part].positive / totalPositive) * 100) : 0
            )
          }
        ]
      }

      // For specific day selection
      const dayDataArray = state.mentionsOverTime.filter((d) => moment(d.date).day() === dayNumber)
      if (!dayDataArray.length) return []

      const daypartData = {
        morning: { positive: 0, negative: 0 },
        afternoon: { positive: 0, negative: 0 },
        evening: { positive: 0, negative: 0 },
        night: { positive: 0, negative: 0 }
      }

      dayDataArray.forEach((dayData) => {
        dayData.part.forEach((part, index) => {
          const partKey = part.toLowerCase()
          daypartData[partKey].positive += dayData.positiveMentionCount
          daypartData[partKey].negative += dayData.negativeMentionCount
        })
      })

      const totalPositive = Object.values(daypartData).reduce((sum, part) => sum + part.positive, 0)
      const totalNegative = Object.values(daypartData).reduce((sum, part) => sum + part.negative, 0)

      if (!totalPositive && !totalNegative) return []

      return [
        {
          name: 'Negative',
          data: ['morning', 'afternoon', 'evening'].map((part) =>
            totalNegative ? Math.round((daypartData[part].negative / totalNegative) * 100) : 0
          )
        },
        {
          name: 'Positive',
          data: ['morning', 'afternoon', 'evening'].map((part) =>
            totalPositive ? Math.round((daypartData[part].positive / totalPositive) * 100) : 0
          )
        }
      ]
    },
    selectHeatmapSeries(state, getters) {
      // Get the feedback breakdown data from state
      const feedbackBreakdown = state.feedbackBreakdown
      const categories = getters.selectHeatmapCategories
      const parentMap = getters.selectCategoryParentMap

      // Transform the API response into the required format
      const seriesData = feedbackBreakdown.map((item) => {
        // Initialize data arrays
        const data = new Array(categories.length).fill(0)
        const counts = new Array(categories.length).fill(0)

        // Set total feedback count in first column
        data[0] = -1 // Special value for feedback count
        counts[0] = item.feedbackCount

        // Create a map to aggregate negative counts by effective category
        const categoryAggregates = {}

        // First aggregate all negative counts by effective category
        item.categoryBreakdown.forEach((cat) => {
          const effectiveCategoryId = parentMap[cat.categoryId] || cat.categoryId
          if (!categoryAggregates[effectiveCategoryId]) {
            categoryAggregates[effectiveCategoryId] = 0
          }
          categoryAggregates[effectiveCategoryId] += cat.negativeMentionCount
        })

        // Then set the aggregated counts and calculate percentages
        Object.entries(categoryAggregates).forEach(([categoryId, totalNegativeCount]) => {
          const categoryIndex = categories.indexOf(categoryId)
          if (categoryIndex > 0) {
            // Skip first column (feedback count)
            counts[categoryIndex] = totalNegativeCount
            if (item.feedbackCount) {
              const percentage = Math.ceil((totalNegativeCount / item.feedbackCount) * 100)
              data[categoryIndex] = percentage <= 100 ? percentage : 100
            }
          }
        })

        const heatmapData = {
          name: item.label,
          locationId: item.locationId,
          data,
          counts,
          totalCount: item.feedbackCount
        }
        return heatmapData
      })

      // reverse to show "All" at the top
      return seriesData.reverse()
    },
    selectFeedbackCount(state) {
      return state.feedbackCount
    },
    selectSubcategories: (state, getters) => (id) => {
      return getters.selectNonOtherCategories.filter((c) => new RegExp(id, 'i').test(c.path))
    },
    selectLocationFeedbackCount: (state) => (id) => {
      const counts = state.feedbackCountBreakdown[id]
      return counts ? counts.reviewCount + counts.surveyCount : 0
    },
    selectTopDrivers(state) {
      return state.topDrivers
    },
    selectSummaries: (state) => state.summaries,
    selectFeedbackDropdown: (state) => state.feedbackDropdown
  },
  mutations: {
    SET_CLASSIFICATIONS(state, classifications) {
      state.classifications = classifications
    },
    SET_NEGATIVE_CLASSIFICATIONS(state, negativeClassifications) {
      state.negativeClassifications = negativeClassifications
    },
    SET_COMPANY_WIDE_NEGATIVE_CLASSIFICATIONS(state, negativeClassifications) {
      state.companyWideNegativeClassifications = negativeClassifications
    },
    SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY(state, positiveClassificationsByCategory) {
      state.positiveClassificationsByCategory = positiveClassificationsByCategory
    },
    SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY(state, negativeClassificationsByCategory) {
      state.negativeClassificationsByCategory = negativeClassificationsByCategory
    },
    SET_TOTAL_MENTIONS(state, totalMentions) {
      state.totalMentions = totalMentions
    },
    SET_OVERALL_MENTIONS_BY_DAYPART(state, overallMentionsByDaypart) {
      state.overallMentionsByDaypart = overallMentionsByDaypart
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    SET_FEEDBACK_COUNT(state, feedbackCount) {
      state.feedbackCount = feedbackCount
    },
    SET_TOTAL_FEEDBACK_COUNT(state, totalFeedbackCount) {
      state.totalFeedbackCount = totalFeedbackCount
    },
    SET_TOP_DRIVERS(state, drivers) {
      state.topDrivers = drivers
    },
    SET_SUMMARIES(state, summaries) {
      state.summaries = summaries
    },
    SET_LAST_SUMMARY_URL(state, url) {
      state.lastSummaryURL = url
    },
    SET_FEEDBACK_BREAKDOWN(state, breakdown) {
      state.feedbackBreakdown = breakdown
    },
    SET_MENTIONS_OVER_TIME(state, mentionsOverTime) {
      state.mentionsOverTime = mentionsOverTime
    },
    SET_FEEDBACK_DROPDOWN(state, feedbackDropdown) {
      state.feedbackDropdown = feedbackDropdown
    }
  },
  actions: {
    async applyFilters({ commit, getters }, filters) {
      const { selectFilters } = getters
      if (isEqual(filters, selectFilters)) return
      commit('SET_FILTERS', filters)
    },

    async setLastSummaryUrl({ commit }, url) {
      commit('SET_LAST_SUMMARY_URL', url)
    },

    async formatFilters({ getters, rootState, rootGetters }, { allLocations = false } = {}) {
      const { activeCompany } = rootState.company
      const filters = getters.selectFilters
      const payloadFilters = {
        createdAtRange: filters.dateRange || [
          moment().startOf('d').subtract(30, 'd').utc().format(),
          moment().endOf('day').utc().format()
        ]
      }

      // Handle companies filtering
      if (allLocations) {
        // For company-wide view, include all available companies
        payloadFilters.companyIds = rootGetters['company/selectInsightsCompanies'].map((c) => c._id)
      } else if (activeCompany._id === 'ALL_COMPANIES') {
        if (filters.companies && filters.companies.length) {
          payloadFilters.companyIds = Array.isArray(filters.companies)
            ? filters.companies
            : [filters.companies]
        } else {
          payloadFilters.companyIds = rootGetters['company/selectInsightsCompanies'].map(
            (c) => c._id
          )
        }
      } else {
        payloadFilters.companyIds = [activeCompany._id]
      }

      // Handle locations filtering
      if (filters.locations && !allLocations) {
        // Apply location filter only when not requesting all locations
        payloadFilters.locationIds = Array.isArray(filters.locations)
          ? filters.locations
          : [filters.locations]
      } else if (allLocations) {
        // Get all locations for company-wide view or when no location filter is applied
        payloadFilters.locationIds = rootGetters['location/selectInsightsLocations']
          .filter((l) => !l.hideFromReports && !l.disabled)
          .map((l) => l._id)
      }

      if (filters.groups && filters.groups.length) {
        payloadFilters.groupIds = Array.isArray(filters.groups) ? filters.groups : [filters.groups]
      }

      // Include source filters if present
      if (filters.sources?.length) {
        payloadFilters.sources = filters.sources
      }

      return payloadFilters
    },

    async fetchCategories({ commit }) {
      const response = await InsightsService.fetchCategories()
      commit('SET_CATEGORIES', response.body.data.categories)
    },

    async fetchBreakdown({ commit, dispatch, getters }) {
      const filters = getters.selectFilters
      const response = await InsightsService.fetchBreakdown({
        filters: await dispatch('formatFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {})
      })

      const allPcs = response.body.data.feedbackBreakdown.reduce((acc, item, index) => {
        if (index === 0) return acc
        item.categoryBreakdown.forEach((category) => {
          if (!acc[category.categoryId]) {
            acc[category.categoryId] = {
              name: category.categoryName,
              classifications: [{ positiveMentionCount: 0 }]
            }
          }
          acc[category.categoryId].classifications[0].positiveMentionCount +=
            category.positiveMentionCount
        })
        return acc
      }, {})
      const allNcs = response.body.data.feedbackBreakdown.reduce((acc, item, index) => {
        if (index === 0) return acc
        item.categoryBreakdown.forEach((category) => {
          if (!acc[category.categoryId]) {
            acc[category.categoryId] = {
              name: category.categoryName,
              classifications: [{ negativeMentionCount: 0 }]
            }
          }
          acc[category.categoryId].classifications[0].negativeMentionCount +=
            category.negativeMentionCount
        })
        return acc
      }, {})

      commit('SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY', allPcs)
      commit('SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY', allNcs)
      commit('SET_FEEDBACK_BREAKDOWN', response.body.data.feedbackBreakdown)
      commit('SET_MENTIONS_OVER_TIME', response.body.data.mentionsOverTime)
      commit(
        'SET_FEEDBACK_COUNT',
        response.body.data.feedbackBreakdown.reduce((acc, item) => acc + item.totalFeedbackCount, 0)
      )
    },

    async fetchSummaries({ commit, dispatch, getters }) {
      const filters = getters.selectFilters
      const response = await InsightsService.fetchSummaries({
        filters: await dispatch('formatFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {})
      })
      const summaries = response.body.data
      commit('SET_SUMMARIES', summaries)
    }
  }
}
